import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  AlertTriangle,
  ArrowLeft,
  Crown,
  Settings2,
  Sparkles,
} from 'lucide-react';
import {
  api,
  canEdit,
  canManageUsers,
  RANK_LABEL,
  type AuthUser,
  type VsOverview,
  type VsOverviewPlayer,
  type VsOverviewWeek,
  type VsPolicy,
} from '@/lib/api';
import { cn } from '@/lib/utils';
import { swatchClass, weekSeverity, type VsSeverity } from '@/lib/vsSeverity';
import VsWeekBox from '@/components/VsWeekBox';
import { EventsTabs } from './Events';

type Severity = VsSeverity;

const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'] as const;

type OverviewView = 'weekly' | 'daily';

export default function EventsVsOverview({ user }: { user: AuthUser }) {
  const query = useQuery({ queryKey: ['vs-overview'], queryFn: api.getVsOverview });
  const [view, setView] = useState<OverviewView>('weekly');

  if (!canEdit(user.role)) {
    return (
      <div className="space-y-4">
        <EventsTabs user={user} />
        <div className="text-slate-400">Only R4+ can view VS.</div>
      </div>
    );
  }

  if (query.isLoading) {
    return (
      <div className="space-y-4">
        <EventsTabs user={user} />
        <div className="text-center text-slate-500">Loading…</div>
      </div>
    );
  }

  const data = query.data;
  if (!data) return null;

  return (
    <div className="space-y-4">
      <EventsTabs user={user} />
      <div className="flex items-center justify-between gap-3">
        <Link
          to="/events/vs"
          className="inline-flex items-center gap-1 text-sm text-slate-400 hover:text-white"
        >
          <ArrowLeft size={14} /> Back to VS list
        </Link>
        <div className="flex items-center gap-3">
          <div className="inline-flex rounded-lg border border-slate-800 bg-slate-950 p-0.5 text-xs">
            {(['weekly', 'daily'] as const).map((v) => (
              <button
                key={v}
                type="button"
                onClick={() => setView(v)}
                className={cn(
                  'rounded px-3 py-1 transition',
                  view === v
                    ? 'bg-indigo-600 text-white'
                    : 'text-slate-400 hover:text-white',
                )}
              >
                {v === 'weekly' ? 'Weekly' : 'Daily'}
              </button>
            ))}
          </div>
          <div className="text-xs text-slate-500">
            {data.weeks.length} weeks · {data.players.length} players
          </div>
        </div>
      </div>

      <Legend policy={data.policy} lowThresholdM={data.lowTotalThresholdM} />

      {canManageUsers(user.role) && <PolicyEditor policy={data.policy} />}

      {data.weeks.length === 0 ? (
        <div className="rounded-lg border border-slate-800 bg-slate-900/40 px-4 py-12 text-center text-slate-500">
          No VS entries yet.
        </div>
      ) : (
        <OverviewTable data={data} view={view} />
      )}
    </div>
  );
}

function OverviewTable({ data, view }: { data: VsOverview; view: OverviewView }) {
  const weeks = data.weeks;
  const players = useMemo(() => data.players, [data.players]);

  return (
    <div className="overflow-x-auto rounded-lg border border-slate-800">
      <table className="min-w-full text-sm">
        <thead className="bg-slate-900 text-xs uppercase tracking-wider text-slate-400">
          <tr>
            <th className="sticky left-0 z-10 bg-slate-900 px-3 py-2 text-left">Player</th>
            <th className="px-3 py-2 text-right">Total (M)</th>
            <th className="px-3 py-2 text-right">Highest</th>
            <th className="px-3 py-2 text-right">Lowest</th>
            <th className="px-3 py-2 text-right">Avg</th>
            <th className="px-3 py-2 text-left">History (newest → oldest)</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-800">
          {players.map((p) => (
            <PlayerRow
              key={p.playerId}
              player={p}
              weeks={weeks}
              lowThresholdM={data.lowTotalThresholdM}
              policy={data.policy}
              view={view}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function PlayerRow({
  player,
  weeks,
  lowThresholdM,
  policy,
  view,
}: {
  player: VsOverviewPlayer;
  weeks: VsOverviewWeek[];
  lowThresholdM: number;
  policy: VsPolicy;
  view: OverviewView;
}) {
  const label = player.displayName?.trim() || player.name;

  const values = useMemo(() => {
    const arr: number[] = [];
    for (const w of weeks) {
      const s = player.scores[w.id];
      if (s != null) arr.push(s);
    }
    return arr;
  }, [player.scores, weeks]);

  const highest = values.length ? Math.max(...values) : null;
  const lowest = values.length ? Math.min(...values) : null;
  const avg = values.length ? values.reduce((a, b) => a + b, 0) / values.length : null;

  return (
    <tr className={cn(!player.active && 'opacity-60')}>
      <td
        className={cn(
          'sticky left-0 z-10 bg-slate-950 px-3 py-2 font-medium',
          player.flagLowTotal && 'bg-red-950/70 text-red-100',
        )}
      >
        <div className="flex items-center gap-2">
          {player.flagLowTotal && (
            <span
              title={`Average below ${lowThresholdM} M`}
              className="inline-flex text-red-300"
            >
              <AlertTriangle size={13} />
            </span>
          )}
          <span>{label}</span>
          <span className="text-xs text-slate-500">{RANK_LABEL[player.rank]}</span>
        </div>
      </td>
      <td
        className={cn(
          'px-3 py-2 text-right font-semibold tabular-nums',
          player.flagLowTotal && 'text-red-200',
        )}
      >
        {player.totalM.toLocaleString()}
      </td>
      <td className="px-3 py-2 text-right tabular-nums text-slate-300">
        {highest != null ? highest.toLocaleString() : <span className="text-slate-600">—</span>}
      </td>
      <td className="px-3 py-2 text-right tabular-nums text-slate-300">
        {lowest != null ? lowest.toLocaleString() : <span className="text-slate-600">—</span>}
      </td>
      <td className="px-3 py-2 text-right tabular-nums text-slate-300">
        {avg != null ? Math.round(avg).toLocaleString() : <span className="text-slate-600">—</span>}
      </td>
      <td className="px-3 py-2">
        {view === 'daily' ? (
          <div className="flex flex-wrap gap-1">
            {weeks.map((w) => {
              const scoreM = player.scores[w.id] ?? null;
              const daily = player.dailyByWeek[w.id];
              const dayWins = new Set(player.dayWinsByWeek[w.id] ?? []);
              const days =
                daily && w.dayCutsTenthsM
                  ? daily.map((scoreTenthsM, dayIndex) => ({
                      dayIndex,
                      scoreTenthsM,
                      dayTwentiethTenthsM: w.dayCutsTenthsM![dayIndex] ?? null,
                      dailyWin: dayWins.has(dayIndex),
                    }))
                  : null;
              return (
                <Link key={w.id} to={`/events/vs/${w.id}`} className="block">
                  <VsWeekBox
                    weekLabel={w.weekStart}
                    kind={w.kind}
                    outcome={w.outcome}
                    policy={policy}
                    weekScoreM={scoreM}
                    twentiethM={w.twentiethM}
                    days={days}
                    compact
                  />
                </Link>
              );
            })}
          </div>
        ) : (
        <div className="inline-flex overflow-hidden rounded border border-slate-800 text-[11px] font-semibold leading-none">
          {weeks.map((w, i) => {
            const s = player.scores[w.id];
            const sev: Severity =
              s == null ? 'none' : weekSeverity(s, w.kind, w.twentiethM, policy);
            const isTopScorer = s != null && w.topScorerPlayerId === player.playerId;
            const dayWins = player.dayWinsByWeek[w.id] ?? [];
            const wonDayLabels =
              dayWins.length > 0
                ? dayWins.map((d) => DAY_LABELS[d] ?? `Day ${d}`).join(', ')
                : null;
            const pct =
              s != null && (w.kind === 'push' || w.kind === 'aborted_push') && w.twentiethM != null
                ? ` · ${Math.round((s / w.twentiethM) * 100)}% of top ${policy.topRank}`
                : '';
            const title =
              s == null
                ? `${w.weekStart} · ${w.opponentName} (no score)`
                : `${w.weekStart} · ${w.opponentName} · ${s.toLocaleString()} M${pct}` +
                  (isTopScorer ? ' · TOP SCORER' : '') +
                  (wonDayLabels ? ` · day winner: ${wonDayLabels}` : '');
            return (
              <Link
                key={w.id}
                to={`/events/vs/${w.id}`}
                title={title}
                className={cn(
                  'relative flex h-6 w-6 items-center justify-center transition-colors',
                  swatchClass[sev],
                  i > 0 && 'border-l border-slate-950/60',
                  w.kind === 'save' && sev !== 'none' && 'opacity-70',
                )}
              >
                {sev === 'red-absolute' ? (
                  <AlertTriangle size={12} strokeWidth={3} className="text-white" />
                ) : isTopScorer ? (
                  <Crown size={13} strokeWidth={2.5} className="text-yellow-200 drop-shadow-[0_0_2px_rgba(0,0,0,0.9)]" />
                ) : wonDayLabels ? (
                  <Sparkles size={12} strokeWidth={2.5} className="text-yellow-100 drop-shadow-[0_0_2px_rgba(0,0,0,0.9)]" />
                ) : s == null ? (
                  <span className="opacity-70">—</span>
                ) : (
                  <span className="h-1.5 w-1.5 rounded-full bg-current opacity-70" />
                )}
              </Link>
            );
          })}
        </div>
        )}
      </td>
    </tr>
  );
}

function LegendSwatch({
  sev,
  children,
}: {
  sev: Severity;
  children: ReactNode;
}) {
  return (
    <span
      className={cn(
        'inline-flex h-5 w-5 items-center justify-center rounded-sm',
        swatchClass[sev],
      )}
    >
      {children}
    </span>
  );
}

function Legend({
  policy,
  lowThresholdM,
}: {
  policy: VsPolicy;
  lowThresholdM: number;
}) {
  const orange = Math.round(policy.orangeFactor * 100);
  const red = Math.round(policy.redFactor * 100);
  return (
    <section className="flex flex-wrap items-center gap-x-3 gap-y-2 rounded-lg border border-slate-800 bg-slate-900/40 px-4 py-2 text-xs text-slate-400">
      <span>Per week (push weeks vs. top {policy.topRank}):</span>
      <span className="inline-flex items-center gap-1">
        <LegendSwatch sev="ok">
          <span className="h-1.5 w-1.5 rounded-full bg-current opacity-70" />
        </LegendSwatch>
        good
      </span>
      <span className="inline-flex items-center gap-1">
        <LegendSwatch sev="orange">
          <span className="h-1.5 w-1.5 rounded-full bg-current opacity-70" />
        </LegendSwatch>
        &lt; {orange}%
      </span>
      <span className="inline-flex items-center gap-1">
        <LegendSwatch sev="red">
          <span className="h-1.5 w-1.5 rounded-full bg-current opacity-70" />
        </LegendSwatch>
        &lt; {red}%
      </span>
      <span className="inline-flex items-center gap-1">
        <LegendSwatch sev="red-absolute">
          <AlertTriangle size={10} strokeWidth={3} className="text-white" />
        </LegendSwatch>
        &lt; {policy.redAbsoluteM} M
      </span>
      <span className="inline-flex items-center gap-1">
        <LegendSwatch sev="ok">
          <Crown size={11} strokeWidth={2.5} className="text-yellow-200" />
        </LegendSwatch>
        top scorer
      </span>
      <span className="inline-flex items-center gap-1">
        <LegendSwatch sev="ok">
          <Sparkles size={10} strokeWidth={2.5} className="text-yellow-100" />
        </LegendSwatch>
        day winner
      </span>
      <span className="inline-flex items-center gap-1">
        <LegendSwatch sev="none">
          <span className="opacity-70">—</span>
        </LegendSwatch>
        no score
      </span>
      <span className="ml-2 inline-flex items-center gap-1">
        <AlertTriangle size={12} className="text-red-300" />
        Avg below {lowThresholdM} M
      </span>
    </section>
  );
}

function PolicyEditor({ policy }: { policy: VsPolicy }) {
  const qc = useQueryClient();
  const [expanded, setExpanded] = useState(false);
  const [topRank, setTopRank] = useState(String(policy.topRank));
  const [orangePct, setOrangePct] = useState(String(Math.round(policy.orangeFactor * 100)));
  const [redPct, setRedPct] = useState(String(Math.round(policy.redFactor * 100)));
  const [redAbs, setRedAbs] = useState(String(policy.redAbsoluteM));
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setTopRank(String(policy.topRank));
    setOrangePct(String(Math.round(policy.orangeFactor * 100)));
    setRedPct(String(Math.round(policy.redFactor * 100)));
    setRedAbs(String(policy.redAbsoluteM));
  }, [policy]);

  const save = useMutation({
    mutationFn: () => {
      const tr = Number(topRank);
      const of = Number(orangePct) / 100;
      const rf = Number(redPct) / 100;
      const ra = Number(redAbs);
      if (!Number.isFinite(tr) || tr < 1 || tr > 100)
        throw new Error('Top rank must be 1–100');
      if (!Number.isFinite(of) || of <= 0 || of > 2)
        throw new Error('Orange % must be > 0');
      if (!Number.isFinite(rf) || rf <= 0 || rf > 2) throw new Error('Red % must be > 0');
      if (!Number.isFinite(ra) || ra < 0) throw new Error('Red absolute must be ≥ 0');
      if (rf >= of) throw new Error('Red % must be lower than Orange %');
      return api.updateVsPolicy({
        topRank: Math.round(tr),
        orangeFactor: of,
        redFactor: rf,
        redAbsoluteM: ra,
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['vs-overview'] });
      setError(null);
      setExpanded(false);
    },
    onError: (e) => setError(e instanceof Error ? e.message : 'Save failed'),
  });

  if (!expanded) {
    return (
      <div className="flex items-center justify-end">
        <button
          onClick={() => setExpanded(true)}
          className="inline-flex items-center gap-1 rounded border border-slate-700 px-2 py-1 text-xs text-slate-300 hover:bg-slate-800"
        >
          <Settings2 size={12} /> Policy
        </button>
      </div>
    );
  }

  return (
    <section className="space-y-3 rounded-lg border border-slate-800 bg-slate-900/40 p-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-slate-200">Overview policy</h3>
        <button
          onClick={() => setExpanded(false)}
          className="rounded border border-slate-700 px-2 py-0.5 text-xs text-slate-400 hover:bg-slate-800"
        >
          Close
        </button>
      </div>
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <label className="block text-xs text-slate-400">
          Top-rank reference
          <input
            type="number"
            min={1}
            max={100}
            value={topRank}
            onChange={(e) => setTopRank(e.target.value)}
            className="mt-1 w-full rounded border border-slate-700 bg-slate-950 px-2 py-1 text-sm text-slate-100"
          />
          <span className="mt-0.5 block text-[10px] text-slate-500">Nth player used</span>
        </label>
        <label className="block text-xs text-slate-400">
          Orange threshold (%)
          <input
            type="number"
            min={1}
            max={200}
            value={orangePct}
            onChange={(e) => setOrangePct(e.target.value)}
            className="mt-1 w-full rounded border border-slate-700 bg-slate-950 px-2 py-1 text-sm text-slate-100"
          />
        </label>
        <label className="block text-xs text-slate-400">
          Red threshold (%)
          <input
            type="number"
            min={1}
            max={200}
            value={redPct}
            onChange={(e) => setRedPct(e.target.value)}
            className="mt-1 w-full rounded border border-slate-700 bg-slate-950 px-2 py-1 text-sm text-slate-100"
          />
        </label>
        <label className="block text-xs text-slate-400">
          Absolute red (M)
          <input
            type="number"
            min={0}
            step="0.1"
            value={redAbs}
            onChange={(e) => setRedAbs(e.target.value)}
            className="mt-1 w-full rounded border border-slate-700 bg-slate-950 px-2 py-1 text-sm text-slate-100"
          />
        </label>
      </div>
      {error && <div className="text-sm text-red-400">{error}</div>}
      <div className="flex justify-end">
        <button
          onClick={() => save.mutate()}
          disabled={save.isPending}
          className="rounded bg-indigo-600 px-3 py-1.5 text-sm text-white hover:bg-indigo-500 disabled:opacity-50"
        >
          {save.isPending ? 'Saving…' : 'Save policy'}
        </button>
      </div>
    </section>
  );
}
